import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { MusicTrack, SceneType, VideoFormat } from '@hyvay/video-engine';
import type { PostStatus, SocialPlatform } from '../../services/social.service';
import { errorText } from '../../services/video.service';

/**
 * Primitives of the video studio (2026-11 "HyVay Studio - video" design), in
 * English: dropdowns, switches, checkboxes, radios, the date / time pickers,
 * drawer + dialog shells, platform icons and the label tables. Inline styles
 * against the `--hv-*` tokens; hovers and keyframes live in VS_CSS.
 */

export const VS_FONT = "'Inter',system-ui,sans-serif";
export const VS_SERIF = "'Instrument Serif',Georgia,serif";
export const BRAND = 'var(--brand,#13422C)';

/** Hover / focus states the inline styles cannot express (the design's style-hover). */
export const VS_CSS = `
@keyframes hvSlideIn { from { transform: translateX(calc(100% + 16px)); } to { transform: translateX(0); } }
@keyframes hvDdIn { from { transform: translateY(6px); opacity: 0; } to { transform: none; opacity: 1; } }
.hv-vs .hv-vs-mi:hover:not(:disabled) { background: var(--hv-mint) !important; color: var(--hv-mint-ink) !important; }
.hv-vs .hv-vs-opt:hover:not(:disabled) { background: var(--hv-page-2) !important; }
.hv-vs .hv-vs-row:hover { background: var(--hv-page-2) !important; }
.hv-vs .hv-vs-bd:hover:not(:disabled) { border-color: var(--hv-brand-ink) !important; }
.hv-vs .hv-vs-bd2:hover:not(:disabled) { border-color: var(--hv-line-2) !important; }
.hv-vs .hv-vs-mint:hover:not(:disabled) { background: var(--hv-mint) !important; color: var(--hv-mint-ink) !important; border-color: var(--hv-mint) !important; }
.hv-vs .hv-vs-ghost:hover:not(:disabled) { background: var(--hv-page-2) !important; color: var(--hv-ink) !important; }
.hv-vs .hv-vs-muted:hover { color: var(--hv-mint-ink) !important; }
.hv-vs .hv-vs-under:hover { text-decoration: underline !important; }
.hv-vs .hv-vs-brand:hover:not(:disabled) { background: color-mix(in srgb,var(--brand,#13422C) 82%,black) !important; }
.hv-vs .hv-vs-deep:hover:not(:disabled) { background: var(--hv-deep) !important; }
.hv-vs .hv-vs-danger:hover { background: color-mix(in srgb,var(--hv-danger) 85%,black) !important; }
.hv-vs .hv-vs-in:focus, .hv-vs .hv-vs-in:focus-within { border-color: var(--hv-brand-ink) !important; }
.hv-vs .hv-vs-steps:hover { color: var(--hv-ink) !important; }
.hv-vs .hv-composer:focus-within { border-color: var(--brand,#13422C) !important; box-shadow: 0 0 0 4px color-mix(in srgb, var(--brand,#13422C) 14%, transparent) !important; }
.hv-vs ::-webkit-scrollbar { width: 8px; height: 8px; }
.hv-vs ::-webkit-scrollbar-thumb { background: var(--hv-line-2); border-radius: 99px; }
.hv-vs ::-webkit-scrollbar-track { background: transparent; }
@media (max-width: 1320px) { .hv-vs-chat { max-width: 40vw; } .hv-vs-insp { width: 272px !important; } .hv-vs-dl { display: none !important; } }
@media (max-width: 1140px) { .hv-vs-tab-label { display: none !important; } .hv-vs-chat { max-width: 44vw; } }
@media (max-width: 1040px) { .hv-vs-insp { display: none !important; } }
`;

/* ------------------------------------------------------------------ labels */

/** The storyboard "beat" of each scene type (timeline cards + inspector). */
export const BEAT: Record<SceneType, string> = {
    title: 'Hook', statement: 'Message', screenshot: 'Website', image: 'Photo', features: 'Highlights',
    stat: 'Numbers', quote: 'Testimonial', video: 'Clip', cta: 'End card',
};

export const ASPECTS: { value: VideoFormat; label: string; hint: string; w: number; h: number }[] = [
    { value: 'portrait', label: '9:16', hint: 'Reels, TikTok, Shorts', w: 8, h: 13 },
    { value: 'square', label: '1:1', hint: 'Feed posts', w: 11, h: 11 },
    { value: 'landscape', label: '16:9', hint: 'YouTube, website hero', w: 14, h: 8 },
];
export const aspectLabel = (f: VideoFormat | null | undefined) => ASPECTS.find((a) => a.value === f)?.label ?? '';

export const TRACKS: { value: MusicTrack; label: string; meta: string }[] = [
    { value: 'upbeat', label: 'Upbeat', meta: 'Cheerful, bright energy' },
    { value: 'calm', label: 'Calm', meta: 'Relaxed, easy pace' },
    { value: 'corporate', label: 'Corporate', meta: 'Clean, professional' },
    { value: 'none', label: 'No music', meta: 'Voice only' },
];

export const RENDER_STATUS: Record<string, string> = { QUEUED: 'Queued', RENDERING: 'Rendering', DONE: 'Done', FAILED: 'Failed' };
export const POST_STATUS: Record<PostStatus, string> = { SCHEDULED: 'Scheduled', PUBLISHING: 'Posting', PUBLISHED: 'Posted', FAILED: 'Failed', CANCELED: 'Canceled' };
export const POST_BADGE: Record<PostStatus, string> = {
    SCHEDULED: 'var(--hv-sand)', PUBLISHING: 'var(--hv-mint)', PUBLISHED: 'var(--hv-mint)', FAILED: 'var(--hv-danger-bg)', CANCELED: 'var(--hv-page-2)',
};
export const TIKTOK_PRIVACY: Record<string, string> = {
    PUBLIC_TO_EVERYONE: 'Everyone', MUTUAL_FOLLOW_FRIENDS: 'Friends', FOLLOWER_OF_CREATOR: 'Followers', SELF_ONLY: 'Only me',
};
export const PLATFORM_NAME: Record<SocialPlatform, string> = {
    youtube: 'YouTube', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads',
};

/** English error line from an axios error. */
export function vsError(e: unknown, fallback = 'Something went wrong.'): string {
    const m = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
    if (m === 'Insufficient credits') return 'Not enough credits. Top up first.';
    return errorText(e, fallback);
}

/** 0:07, 1:32 */
export const fmtT = (sec: number) => {
    const s = Math.max(0, sec);
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
export const fmtMB = (bytes: number | null | undefined) => (bytes ? `${(bytes / 1e6).toFixed(1)} MB` : '');

const MS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n: number) => String(n).padStart(2, '0');

/** "Wed, 5 Nov · 18:20" */
export const fmtWhenShort = (d: Date) => `${DW[d.getDay()]}, ${d.getDate()} ${MS[d.getMonth()]} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
/** "Wed, 5 Nov 2026" */
export const fmtDateLong = (d: Date) => `${DW[d.getDay()]}, ${d.getDate()} ${MS[d.getMonth()]} ${d.getFullYear()}`;
export const fmtTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
/** "04/11 14:20" */
export const fmtStamp = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const joinLocal = (day: string, time: string) => new Date(`${day}T${time}`);

/** "Asia/Jakarta (GMT+7)" for the viewer's own zone. */
export const TZ_NAME = (() => {
    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ignore */ }
    const off = -new Date().getTimezoneOffset();
    const h = Math.trunc(Math.abs(off) / 60);
    const m = Math.abs(off) % 60;
    const gmt = `GMT${off >= 0 ? '+' : '-'}${h}${m ? `:${pad(m)}` : ''}`;
    return tz ? `${tz} (${gmt})` : gmt;
})();

/* --------------------------------------------------------------- icons */

const svg = (size: number, sw: number, children: React.ReactNode, style?: React.CSSProperties) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</svg>
);
export const Ico = {
    chevD: (size = 14, style?: React.CSSProperties) => svg(size, 2.2, <path d="M6 9l6 6 6-6" />, { flex: 'none', ...style }),
    chevR: (size = 13, style?: React.CSSProperties) => svg(size, 2.2, <path d="M9 6l6 6-6 6" />, { flex: 'none', ...style }),
    chevL: (size = 14) => svg(size, 2.2, <path d="M15 6l-6 6 6 6" />),
    check: (size = 14, color = 'var(--hv-brand-ink)', sw = 2.6) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M5 12.5l4.5 4.5L19 7" /></svg>
    ),
    close: (size = 18) => svg(size, 2.4, <path d="M6 6l12 12M18 6L6 18" />),
    film: (size = 11, sw = 2) => svg(size, sw, <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M10.2 9.4v5.2l4.4-2.6z" /></>),
    play: (size = 15) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M8 6.6c0-1.2 1.3-1.9 2.3-1.3l8.4 5.4c.9.6.9 2 0 2.6l-8.4 5.4c-1 .6-2.3-.1-2.3-1.3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>
    ),
    pause: (size = 15) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4.2" height="14" rx="2.1" /><rect x="13.8" y="5" width="4.2" height="14" rx="2.1" /></svg>
    ),
    playCircle: (size = 14) => svg(size, 1.9, <><circle cx="12" cy="12" r="9" /><path d="M10 8.6v6.8l5.6-3.4z" /></>),
    media: (size = 14) => svg(size, 1.9, <><rect x="3" y="4.5" width="18" height="15" rx="2.5" /><path d="M3.6 16.5 8.5 11l4 4 3-2.6 4.4 4.1" /><circle cx="15.4" cy="9" r="1.4" /></>),
    download: (size = 14) => svg(size, 1.9, <path d="M12 3.5v11M7.5 10.5 12 15l4.5-4.5M4 19.5h16" />),
    share: (size = 14) => svg(size, 1.9, <><circle cx="18" cy="5.5" r="2.6" /><circle cx="6" cy="12" r="2.6" /><circle cx="18" cy="18.5" r="2.6" /><path d="M8.3 10.7 15.7 6.8M8.3 13.3l7.4 3.9" /></>),
    plus: (size = 16, sw = 2.2) => svg(size, sw, <path d="M12 5v14M5 12h14" />),
    coins: (size = 12) => svg(size, 1.9, <><circle cx="8" cy="9" r="5.3" /><circle cx="15" cy="15" r="5.3" /></>),
    up: (size = 15) => svg(size, 2.4, <path d="M12 19V5M6 11l6-6 6 6" />),
    upload: (size = 22) => svg(size, 1.8, <><path d="M12 17V5M7 10l5-5 5 5" /><path d="M4.5 19.5h15" /></>),
    music: (size = 26) => svg(size, 1.6, <><path d="M9 18V6l11-2v12" /><circle cx="6.5" cy="18" r="2.5" /><circle cx="17.5" cy="16" r="2.5" /></>),
    mic: (size = 26) => svg(size, 1.6, <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></>),
    calendar: (size = 16) => svg(size, 1.9, <path d="M6.5 5.5h11A2.5 2.5 0 0 1 20 8v9.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5V8a2.5 2.5 0 0 1 2.5-2.5z M4 10h16 M8.5 3.5v3.5 M15.5 3.5v3.5" />),
    calendarBig: (size = 18) => svg(size, 1.8, <><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>),
    clock: (size = 16) => svg(size, 1.9, <><circle cx="12" cy="12" r="8" /><path d="M12 7.5V12l3 2" /></>),
    search: (size = 14) => svg(size, 2, <><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></>),
    collapse: (size = 19) => svg(size, 1.8, <><rect x="3.5" y="4" width="17" height="16" rx="2.4" /><path d="M9.5 4v16M16 9.5 13 12l3 2.5" /></>),
    expand: (size = 18) => svg(size, 1.8, <><rect x="3.5" y="4" width="17" height="16" rx="2.4" /><path d="M9.5 4v16M13 9.5 16 12l-3 2.5" /></>),
    pencil: (size = 13) => svg(size, 1.9, <><path d="M4 20h4l10-10a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6z" /><path d="M13.4 6.6 17 10.2" /></>),
    x: (size = 10) => svg(size, 2.6, <path d="M6 6l12 12M18 6L6 18" />),
    external: (size = 13) => svg(size, 1.9, <><path d="M14 4.5h5.5V10" /><path d="M19.5 4.5 11 13" /><path d="M19 14.5v3.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3.5" /></>),
};

const TT_PATH = 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';

/** The coloured platform tile from the design (24 / 22 px). */
export const PlatformIcon: React.FC<{ platform: SocialPlatform; size?: number }> = ({ platform, size = 24 }) => {
    const k = size / 24;
    const box = (bg: string, child: React.ReactNode, extra?: React.CSSProperties) => (
        <span style={{ flex: 'none', width: size, height: size, borderRadius: 6, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', ...extra }}>{child}</span>
    );
    switch (platform) {
        case 'instagram':
            return box('radial-gradient(circle at 30% 107%,#FDD56B 0%,#F8853A 30%,#D6297A 60%,#8A3AB9 85%)', (
                <svg width={16 * k} height={16 * k} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3.5" y="3.5" width="17" height="17" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="1" fill="#fff" stroke="none" /></svg>
            ));
        case 'tiktok':
            return box('#111111', (
                <svg width={15 * k} height={15 * k} viewBox="0 0 24 24"><path fill="#25F4EE" transform="translate(-0.8 -0.6)" d={TT_PATH} /><path fill="#FE2C55" transform="translate(0.8 0.6)" d={TT_PATH} /><path fill="#fff" d={TT_PATH} /></svg>
            ));
        case 'youtube':
            return box('#FFFFFF', (
                <svg width={19 * k} height={14 * k} viewBox="0 0 28 20"><rect x="0" y="0" width="28" height="20" rx="6" fill="#FF0000" /><path d="M11 5.8v8.4l7.2-4.2z" fill="#fff" /></svg>
            ), { border: '1px solid var(--hv-line)' });
        case 'facebook':
            return box('#0866FF', (
                <svg width={16 * k} height={16 * k} viewBox="0 0 24 24"><path fill="#fff" d="M15.6 7.6h-2c-.6 0-1 .5-1 1.1v2h3l-.4 3.1h-2.6V22h-3.2v-8.2H7v-3.1h2.4V8.4c0-2.6 1.5-4.1 3.9-4.1 1.1 0 2 .1 2.3.1v3.2z" /></svg>
            ));
        case 'linkedin':
            return box('#0A66C2', <span style={{ color: '#fff', fontSize: 12 * k, fontWeight: 800, fontFamily: 'Arial, sans-serif', letterSpacing: '-0.02em' }}>in</span>);
        case 'threads':
            return box('#101010', <span style={{ color: '#fff', fontSize: 14 * k, fontWeight: 700, lineHeight: 1 }}>@</span>);
    }
};

/* ------------------------------------------------------------ form bits */

export const fieldLabel: React.CSSProperties = { fontSize: 12, color: 'var(--hv-body)' };
export const inputStyle: React.CSSProperties = {
    width: '100%', height: 36, padding: '0 11px', borderRadius: 6, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)',
    color: 'var(--hv-ink)', font: 'inherit', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
export const textareaStyle: React.CSSProperties = { ...inputStyle, height: 'auto', padding: '10px 11px', lineHeight: 1.5, resize: 'vertical' };

/** Label + input, stacked (inspector fields). */
export const LabeledInput: React.FC<{ label: string; value: string; placeholder?: string; onChange: (v: string) => void; type?: string }> = ({ label, value, placeholder, onChange, type }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...fieldLabel }}>
        {label}
        <input className="hv-vs-in" type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </label>
);

/** The design's 28×16 toggle. */
export const Switch: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: React.ReactNode; disabled?: boolean; title?: string; compact?: boolean }> = ({ on, onChange, label, disabled, title, compact }) => (
    <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        title={title}
        onClick={() => onChange(!on)}
        style={{ display: 'flex', alignItems: compact ? 'center' : 'flex-start', gap: 10, width: '100%', padding: compact ? 0 : '5px 0', minHeight: compact ? 18 : undefined, border: 0, background: 'transparent', color: disabled ? 'var(--hv-muted)' : 'var(--hv-ink)', fontSize: 13, lineHeight: 1.45, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
        <span style={{ flex: 'none', marginTop: compact ? 0 : 1, width: 28, height: 16, borderRadius: 999, background: on && !disabled ? BRAND : 'var(--hv-line-2)', position: 'relative', transition: 'background 0.2s ease', opacity: disabled ? 0.6 : 1 }}>
            <span style={{ position: 'absolute', top: 2, left: on ? 14 : 2, width: 12, height: 12, borderRadius: 999, background: '#FFFDF7', transition: 'left 0.2s ease' }} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
    </button>
);

/** Square checkbox + label (post drawer). */
export const Check: React.FC<{ on: boolean; onChange: (v: boolean) => void; children: React.ReactNode; disabled?: boolean }> = ({ on, onChange, children, disabled }) => (
    <button type="button" role="checkbox" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, textAlign: 'left' }}>
        <CheckBox on={on} />
        <span>{children}</span>
    </button>
);
export const CheckBox: React.FC<{ on: boolean; partial?: boolean }> = ({ on, partial }) => (
    <span style={{ flex: 'none', width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on || partial ? BRAND : 'var(--hv-line-2)'}`, background: on || partial ? BRAND : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {on && Ico.check(10, '#FFFDF7', 3.4)}
        {!on && partial && <span style={{ width: 8, height: 2, borderRadius: 1, background: '#FFFDF7' }} />}
    </span>
);

/** Round radio + label. */
export const Radio: React.FC<{ on: boolean; onPick: () => void; children: React.ReactNode; disabled?: boolean; size?: number }> = ({ on, onPick, children, disabled, size = 16 }) => (
    <button type="button" role="radio" aria-checked={on} disabled={disabled} onClick={onPick} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, textAlign: 'left' }}>
        <span style={{ flex: 'none', width: size, height: size, borderRadius: 999, border: `1.5px solid ${on ? BRAND : 'var(--hv-line-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.15s ease' }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: BRAND, opacity: on ? 1 : 0, transition: 'opacity 0.15s ease' }} />
        </span>
        {children}
    </button>
);

const popStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, padding: 6, borderRadius: 8, background: 'var(--hv-card)',
    border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvDdIn 0.18s ease both',
};
export const ddButton = (open: boolean, disabled?: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${open ? 'var(--hv-brand-ink)' : 'var(--hv-line-2)'}`,
    background: 'var(--hv-card)', color: 'var(--hv-ink)', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, fontSize: 13.5,
});

export interface DdOption<T extends string> { value: T; label: string; meta?: string; lead?: React.ReactNode }

/** Inspector dropdown: button + listbox with a click-away layer. */
export function Dropdown<T extends string>({ value, options, onChange, placeholder, disabled, label }: {
    value: T | undefined; options: DdOption<T>[]; onChange: (v: T) => void; placeholder?: string; disabled?: boolean; label?: string;
}) {
    const [open, setOpen] = useState(false);
    const cur = options.find((o) => o.value === value);
    return (
        <div style={{ position: 'relative' }}>
            <button type="button" className="hv-vs-bd" aria-haspopup="listbox" aria-expanded={open} aria-label={label} disabled={disabled} onClick={() => setOpen((o) => !o)} style={ddButton(open, disabled)}>
                {cur?.lead}
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: cur ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>{cur?.label ?? placeholder ?? 'Choose'}</span>
                {Ico.chevD(14, { color: 'var(--hv-muted)', transform: `rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.2s ease' })}
            </button>
            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
                    <div role="listbox" style={popStyle}>
                        {options.map((o) => {
                            const on = o.value === value;
                            return (
                                <button key={o.value} type="button" role="option" aria-selected={on} className="hv-vs-opt" onClick={() => { onChange(o.value); setOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 36, padding: '7px 10px', border: 0, borderRadius: 5, background: on ? 'color-mix(in srgb,var(--hv-mint) 55%,var(--hv-card))' : 'transparent', color: 'var(--hv-ink)', textAlign: 'left', cursor: 'pointer' }}>
                                    {o.lead}
                                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <span style={{ fontSize: 13, fontWeight: 500 }}>{o.label}</span>
                                        {o.meta && <span style={{ fontSize: 11.5, lineHeight: 1.4, color: 'var(--hv-muted)' }}>{o.meta}</span>}
                                    </span>
                                    {on && Ico.check()}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

/** The outlined frame glyph of an aspect ratio. */
export const AspectGlyph: React.FC<{ w: number; h: number }> = ({ w, h }) => (
    <span style={{ flex: 'none', width: 16, display: 'flex', justifyContent: 'center', color: 'var(--hv-body)' }}>
        <span style={{ flex: 'none', width: w, height: h, border: '1.6px solid currentColor', borderRadius: 2.5 }} />
    </span>
);

/* ----------------------------------------------------- date / time pickers */

/** Date button + month grid (past days and days beyond `max` disabled). */
export const DateField: React.FC<{ value: string; onChange: (day: string) => void; max: Date }> = ({ value, onChange, max }) => {
    const [open, setOpen] = useState(false);
    const today = isoDay(new Date());
    const maxIso = isoDay(max);
    const [y, m, d] = value.split('-').map(Number);
    const [month, setMonth] = useState(() => new Date(y, m - 1, 1));
    useEffect(() => { if (open) setMonth(new Date(y, m - 1, 1)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const now = new Date();
    const prevDis = month.getFullYear() * 12 + month.getMonth() <= now.getFullYear() * 12 + now.getMonth();
    const nextDis = month.getFullYear() * 12 + month.getMonth() >= max.getFullYear() * 12 + max.getMonth();
    const cells = useMemo(() => {
        const start = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay());
        const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        const n = Math.ceil((month.getDay() + days) / 7) * 7;
        return Array.from({ length: n }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    }, [month]);
    const dt = new Date(y, m - 1, d);
    return (
        <div style={{ position: 'relative' }}>
            <button type="button" className="hv-vs-bd" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ ...ddButton(open), fontWeight: 500 }}>
                <span style={{ display: 'flex', color: 'var(--hv-body)' }}>{Ico.calendar()}</span>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtDateLong(dt)}</span>
            </button>
            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
                    <div role="dialog" aria-label="Choose date" style={{ ...popStyle, top: 'calc(100% + 4px)', minWidth: 240, zIndex: 31, padding: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 0 8px 6px' }}>
                            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700 }}>{MON[month.getMonth()]} {month.getFullYear()}</span>
                            <button type="button" className="hv-vs-ghost" aria-label="Previous month" disabled={prevDis} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} style={{ width: 28, height: 28, borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: prevDis ? 'default' : 'pointer', opacity: prevDis ? 0.3 : 1 }}>{Ico.chevL()}</button>
                            <button type="button" className="hv-vs-ghost" aria-label="Next month" disabled={nextDis} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} style={{ width: 28, height: 28, borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: nextDis ? 'default' : 'pointer', opacity: nextDis ? 0.3 : 1 }}>{Ico.chevR(14)}</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', rowGap: 2 }}>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((w) => (
                                <span key={w} style={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 600, color: 'var(--hv-muted)' }}>{w}</span>
                            ))}
                            {cells.map((c) => {
                                const k = isoDay(c);
                                const inM = c.getMonth() === month.getMonth();
                                const past = k < today || k > maxIso;
                                const on = k === value;
                                const isToday = k === today;
                                const dis = past || !inM;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        className={dis || on ? undefined : 'hv-vs-opt'}
                                        disabled={dis}
                                        aria-pressed={on}
                                        aria-label={`${c.getDate()} ${MON[c.getMonth()]}`}
                                        onClick={() => { onChange(k); setOpen(false); }}
                                        style={{ justifySelf: 'center', width: 34, height: 34, borderRadius: 999, border: 0, padding: 0, background: on ? BRAND : 'transparent', color: on ? '#FFFDF7' : past ? 'var(--hv-line-2)' : 'var(--hv-ink)', boxShadow: isToday && !on ? 'inset 0 0 0 1.5px var(--hv-brand-ink)' : 'none', fontSize: 12.5, fontWeight: on || isToday ? 700 : 500, cursor: dis ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        {inM ? c.getDate() : ''}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

/** Time button + HOUR / MIN columns; times already past today are disabled. */
export const TimeField: React.FC<{ day: string; value: string; onChange: (t: string) => void }> = ({ day, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const [ch, cm] = value.split(':').map(Number);
    const now = new Date();
    const isToday = day === isoDay(now);
    const nowM = now.getHours() * 60 + now.getMinutes();
    const okAt = (hh: number, mm: number) => !isToday || hh * 60 + mm > nowM;
    const firstMin = (hh: number) => { for (let mm = 0; mm < 60; mm++) if (okAt(hh, mm)) return mm; return -1; };
    useEffect(() => {
        if (!open) return;
        boxRef.current?.querySelectorAll('[data-col]').forEach((c) => {
            const on = c.querySelector('[data-on="1"]') as HTMLElement | null;
            if (on) (c as HTMLElement).scrollTop = on.offsetTop - (c as HTMLElement).clientHeight / 2 + on.offsetHeight / 2 - (c as HTMLElement).offsetTop;
        });
    }, [open]);
    const cell = (on: boolean, dis: boolean, label: string, pick: () => void) => (
        <button key={label} type="button" role="option" aria-selected={on} data-on={on ? '1' : '0'} disabled={dis} onClick={pick} className={dis || on ? undefined : 'hv-vs-opt'} style={{ flex: 'none', width: '100%', height: 32, border: 0, borderRadius: 5, background: on ? BRAND : 'transparent', color: on ? '#FFFDF7' : dis ? 'var(--hv-line-2)' : 'var(--hv-ink)', fontSize: 13, fontWeight: on ? 700 : 500, fontVariantNumeric: 'tabular-nums', cursor: dis ? 'default' : 'pointer' }}>{label}</button>
    );
    const col: React.CSSProperties = { height: 224, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, paddingRight: 2, scrollbarWidth: 'thin' };
    const head: React.CSSProperties = { padding: '2px 0 6px', textAlign: 'center', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--hv-muted)' };
    return (
        <div style={{ position: 'relative' }}>
            <button type="button" className="hv-vs-bd" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ ...ddButton(open), fontWeight: 500 }}>
                <span style={{ display: 'flex', color: 'var(--hv-body)' }}>{Ico.clock()}</span>
                <span style={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </button>
            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
                    <div ref={boxRef} role="dialog" aria-label="Choose time" style={{ ...popStyle, top: 'calc(100% + 4px)', zIndex: 31, padding: 8, display: 'flex', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={head}>HOUR</div>
                            <div data-col="1" style={col}>
                                {Array.from({ length: 24 }, (_, hh) => {
                                    const dis = firstMin(hh) < 0;
                                    return cell(hh === ch, dis, pad(hh), () => onChange(`${pad(hh)}:${pad(okAt(hh, cm) ? cm : firstMin(hh))}`));
                                })}
                            </div>
                        </div>
                        <div style={{ width: 1, background: 'var(--hv-line)', margin: '24px 0 0' }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={head}>MIN</div>
                            <div data-col="1" style={col}>
                                {Array.from({ length: 60 }, (_, mm) => cell(mm === cm, !okAt(ch, mm), pad(mm), () => { onChange(`${pad(ch)}:${pad(mm)}`); setOpen(false); }))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

/* ------------------------------------------------------------- overlays */

/** Right-side drawer (scrim + panel slid in from the right). */
export const Drawer: React.FC<{ title: React.ReactNode; width: number | string; onClose: () => void; theme: 'light' | 'dark'; z?: number; children: React.ReactNode; footer?: React.ReactNode; label: string }> = ({ title, width, onClose, theme, z = 8850, children, footer, label }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="hv-root hv-vs" data-hv-theme={theme} style={{ position: 'fixed', inset: 0, zIndex: z, fontFamily: VS_FONT }}>
            <style>{VS_CSS}</style>
            <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(14,20,17,0.32)', animation: 'hvFade 0.18s ease both' }} />
            <aside role="dialog" aria-modal="true" aria-label={label} style={{ position: 'absolute', top: 8, right: 8, bottom: 8, width: typeof width === 'number' ? `min(${width}px, calc(100% - 16px))` : width, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 10, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 70px -24px rgba(0,0,0,0.35)', color: 'var(--hv-ink)', animation: 'hvSlideIn 0.32s cubic-bezier(0.32,0.72,0,1) both' }}>
                <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px 8px 20px', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)' }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</div>
                    <button type="button" className="hv-vs-ghost" onClick={onClose} aria-label="Close" style={{ flex: 'none', width: 36, height: 36, borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>{Ico.close()}</button>
                </div>
                {children}
                {footer}
            </aside>
        </div>
    );
};

/** Small top-aligned dialog (Cancel this post?, Edit schedule). */
export const VsDialog: React.FC<{ title: string; onClose: () => void; theme: 'light' | 'dark'; children: React.ReactNode; alert?: boolean; z?: number }> = ({ title, onClose, theme, children, alert, z = 9000 }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="hv-root hv-vs" data-hv-theme={theme} style={{ position: 'fixed', inset: 0, zIndex: z, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 20px 20px', fontFamily: VS_FONT }}>
            <style>{VS_CSS}</style>
            <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(14,20,17,0.42)' }} />
            <div role={alert ? 'alertdialog' : 'dialog'} aria-modal="true" aria-label={title} style={{ position: 'relative', width: '100%', maxWidth: 420, padding: '16px 20px 18px', borderRadius: 10, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 60px -24px rgba(0,0,0,0.35)', color: 'var(--hv-ink)', animation: 'hvDdIn 0.2s ease both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '-16px -20px 14px', padding: '8px 16px 8px 20px', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)', borderRadius: '10px 10px 0 0' }}>
                    <div style={{ flex: 1, fontSize: 14.5, fontWeight: 600 }}>{title}</div>
                    <button type="button" className="hv-vs-ghost" onClick={onClose} aria-label="Close" style={{ flex: 'none', width: 36, height: 36, margin: '0 -6px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer' }}>{Ico.close()}</button>
                </div>
                {children}
            </div>
        </div>
    );
};

export const pillBtn = (primary: boolean, disabled?: boolean): React.CSSProperties => (primary
    ? { flex: 'none', whiteSpace: 'nowrap', height: 36, padding: '0 18px', borderRadius: 999, border: 0, background: BRAND, color: '#FFFDF7', fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, display: 'flex', alignItems: 'center', gap: 8 }
    : { flex: 'none', whiteSpace: 'nowrap', height: 36, padding: '0 14px', borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, fontWeight: 400, cursor: 'pointer' });

/** Small spinner (13px, brand ring). */
export const Spin: React.FC<{ size?: number; light?: boolean }> = ({ size = 13, light }) => (
    <span style={{ flex: 'none', display: 'inline-block', width: size, height: size, borderRadius: 999, border: `2px solid ${light ? 'rgba(255,253,247,0.35)' : 'var(--hv-line-2)'}`, borderTopColor: light ? '#FFFDF7' : 'var(--hv-brand-ink)', animation: 'hvSpin 0.8s linear infinite' }} />
);
